import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Grid,
    Skeleton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    useTheme,
} from "@mui/material";
import { Place } from '@mui/icons-material';
import { api } from '../../../../api/api';
import { useAuth } from '../../../../contexts/AuthProvider/AuthProvider';
import { generateYearOptions, MONTHS } from '../../../Reports/ReportHelpers';
import { useBillingCompanyContext } from '../../../../providers';

const COLORS = ["#0d3b66", "#1e5a8e", "#2e7ab8", "#4a9fd8", "#6bb8e8"];

interface VenueData {
    venue_name: string;
    venue_type: string | null;
    seating_capacity: string;
    floating_capacity: number;
    total_leads: number;
    booked: string;
    cancelled: string;
    ongoing: string;
    hold: string;
    revenue: string;
}

const fmtNum = (val: string | number) => {
    return Number(val).toLocaleString();
};

const fmtCurrency = (val: string | number) => {
    return `₹${Number(val).toLocaleString()}`;
};

export interface VenueReportProps {
    frameless?: boolean;
}

export const VenueReport: React.FC<VenueReportProps> = ({ frameless = false }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";
    const { getActiveAuth } = useAuth();
    const activeAuth = getActiveAuth();
    const { comapny: { company_outlet_uuid} } = useBillingCompanyContext();

    // State management
    const currentDate = new Date();
    const [commonYear, setCommonYear] = useState<number>(currentDate.getFullYear());
    const [commonMonth, setCommonMonth] = useState<number | "">(currentDate.getMonth() + 1);
    const [venues, setVenues] = useState<VenueData[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    // Fetch data from API
    const fetchVenuePerformance = async () => {
        try {
            setLoading(true);

            // Calculate date range
            let fromDate: string;
            let toDate: string;

            if (commonMonth === "") {
                // All months - full year
                fromDate = `${commonYear}-01-01`;
                toDate = `${commonYear}-12-31`;
            } else {
                // Specific month
                const year = commonYear;
                const month = commonMonth;
                const lastDay = new Date(year, month, 0).getDate();
                fromDate = `${year}-${String(month).padStart(2, '0')}-01`;
                toDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
            }

            const response = await api.get('/report/get-venue-performance-overview', {
                params: {
                    company_outlet_uuid: company_outlet_uuid,
                    from_date: fromDate,
                    to_date: toDate,
                },
            });

            if (response.data.data && Array.isArray(response.data.data)) {
                setVenues(response.data.data);
            }
        } catch (error) {
            console.error("Error fetching venue performance:", error);
            setVenues([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch data on mount and when filters change
    useEffect(() => {
        if (company_outlet_uuid) {
            fetchVenuePerformance();
        }
    }, [commonYear, commonMonth, company_outlet_uuid]);

    const selectSx = frameless
        ? {
            borderRadius: "12px",
            fontSize: "0.82rem",
            fontWeight: 600,
            backgroundColor: isDark ? "rgba(30,41,59,0.5)" : "#ffffff",
            "& .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.08)",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)",
            },
        }
        : {};

    // --- DESKTOP VIEW (Original untouched layout & design for big screens) ---
    if (!frameless) {
        return (
            <Box
                sx={{
                    gridColumn: {
                        xs: "span 2",
                        md: "span 4",
                        lg: "span 6",
                    },
                    borderRadius: 2,
                    backgroundColor: "#fff",
                    p: 2.5,
                }}
            >
                <Box
                    sx={{
                        mb: 2,
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        alignItems: { xs: "flex-start", md: "center" },
                        justifyContent: "space-between",
                        gap: { xs: 1.5, md: 2 },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Place
                            sx={{ fontSize: 20, color: "#ec4899" }}
                        />

                        <Typography fontWeight="bold" variant="h3">
                            Venues
                        </Typography>
                    </Box>

                    {/* Filters */}
                    <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-end", gap: 2, width: { xs: "100%", md: "auto" } }}>
                        <FormControl size="small" sx={{ flex: { xs: 1, md: "none" }, minWidth: { xs: 0, md: 120 } }}>
                            <InputLabel>Year</InputLabel>
                            <Select
                                value={commonYear}
                                label="Year"
                                size='small'
                                onChange={(e) => setCommonYear(Number(e.target.value))}
                                disabled={loading}
                            >
                                {generateYearOptions().map((y) => (
                                    <MenuItem key={y} value={y}>
                                        {y}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ flex: { xs: 1, md: "none" }, minWidth: { xs: 0, md: 120 } }}>
                            <InputLabel>Month</InputLabel>
                            <Select
                                value={commonMonth}
                                label="Month"
                                onChange={(e) => setCommonMonth(e.target.value === "" ? "" : Number(e.target.value))}
                                disabled={loading}
                                renderValue={(value) => {
                                    if (!value) return "All Months";
                                    const month = MONTHS.find((m) => m.value === value);
                                    return month ? month.label : "";
                                }}
                            >
                                <MenuItem value="">All Months</MenuItem>
                                {MONTHS.map((m) => (
                                    <MenuItem key={m.value} value={m.value}>
                                        {m.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Loading State */}
                {loading ? (
                    <Grid container spacing={2}>
                        {[1, 2].map((i) => (
                            <Grid item xs={12} md={6} key={i}>
                                <Skeleton
                                    variant="rectangular"
                                    height={180}
                                    sx={{ borderRadius: "20px" }}
                                />
                            </Grid>
                        ))}
                    </Grid>
                ) : venues.length === 0 ? (
                    // Empty State
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: 200,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            No venue data available for the selected period
                        </Typography>
                    </Box>
                ) : (
                    // Data Grid
                    <Grid container spacing={2}>
                        {venues.map((v, i) => {
                            const conv = v.total_leads
                                ? (Number(v.booked) / v.total_leads) * 100
                                : 0;

                            return (
                                <Grid
                                    item
                                    xs={12}
                                    md={6}
                                    key={v.venue_name}
                                >
                                    <Box
                                        sx={{
                                            border: "1px dashed black",
                                            borderRadius: "20px",
                                            p: 2,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                justifyContent: "space-between",
                                            }}
                                        >
                                            <Box>
                                                <Typography
                                                    variant="h5"
                                                    fontWeight={700}
                                                    textTransform="capitalize"
                                                >
                                                    {v.venue_name}
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    sx={{ color: "#eae9e999" }}
                                                >
                                                    {v.venue_type || "Indoor"} ·{" "}
                                                    {fmtNum(v.seating_capacity)} seats
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    px: 1.5,
                                                    py: 0.5,
                                                    borderRadius: "999px",
                                                    fontSize: "12px",
                                                    fontWeight: "bold",
                                                    color: "#fff",
                                                    background: COLORS[i % COLORS.length],
                                                }}
                                            >
                                                {conv.toFixed(0)}%
                                            </Box>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "repeat(3, 1fr)",
                                                gap: 1,
                                                textAlign: "center",
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    borderRadius: "10px",
                                                    backgroundColor: "#f2f2f2",
                                                    py: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight={600}
                                                >
                                                    Leads
                                                </Typography>

                                                <Typography fontWeight="bold">
                                                    {v.total_leads}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    borderRadius: "10px",
                                                    backgroundColor: "#f2f2f2",
                                                    py: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight={600}
                                                >
                                                    Booked
                                                </Typography>

                                                <Typography fontWeight="bold">
                                                    {v.booked}
                                                </Typography>
                                            </Box>

                                            <Box
                                                sx={{
                                                    borderRadius: "10px",
                                                    backgroundColor: "#f2f2f2",
                                                    py: 1,
                                                }}
                                            >
                                                <Typography
                                                    variant="caption"
                                                    fontWeight={600}
                                                >
                                                    Revenue
                                                </Typography>

                                                <Typography fontWeight="bold">
                                                    {fmtCurrency(v.revenue)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>
        );
    }

    // --- MOBILE VIEW (Frameless, sleek mobile dashboard design) ---
    return (
        <Box
            sx={{
                width: "100%",
                borderRadius: 0,
                backgroundColor: "transparent",
                boxShadow: "none",
                border: "none",
                p: 0,
            }}
        >
            <Box
                sx={{
                    mb: 2,
                    display: "flex",
                    flexDirection: { xs: "row", md: "row" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <Box
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: "10px",
                            backgroundColor: "rgba(236, 72, 153, 0.12)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        <Place sx={{ fontSize: 18, color: "#ec4899" }} />
                    </Box>

                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            fontSize: "1.15rem",
                            color: theme.palette.text.primary,
                            letterSpacing: "-0.01em",
                        }}
                    >
                        Venues
                    </Typography>
                </Box>

                {/* Filters */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <FormControl size="small" sx={{ minWidth: 90 }}>
                        <InputLabel id="venue-year-label" sx={{ fontSize: "0.8rem" }}>Year</InputLabel>
                        <Select
                            labelId="venue-year-label"
                            value={commonYear}
                            label="Year"
                            onChange={(e) => setCommonYear(Number(e.target.value))}
                            disabled={loading}
                            sx={selectSx}
                        >
                            {generateYearOptions().map((y) => (
                                <MenuItem key={y} value={y} sx={{ fontSize: "0.82rem" }}>
                                    {y}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <InputLabel id="venue-month-label" sx={{ fontSize: "0.8rem" }}>Month</InputLabel>
                        <Select
                            labelId="venue-month-label"
                            value={commonMonth}
                            label="Month"
                            onChange={(e) => setCommonMonth(e.target.value === "" ? "" : Number(e.target.value))}
                            disabled={loading}
                            sx={selectSx}
                            renderValue={(value) => {
                                if (!value) return "All";
                                const month = MONTHS.find((m) => m.value === value);
                                return month ? month.label.substring(0, 3) : "";
                            }}
                        >
                            <MenuItem value="" sx={{ fontSize: "0.82rem" }}>All Months</MenuItem>
                            {MONTHS.map((m) => (
                                <MenuItem key={m.value} value={m.value} sx={{ fontSize: "0.82rem" }}>
                                    {m.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Loading State */}
            {loading ? (
                <Grid container spacing={2}>
                    {[1, 2].map((i) => (
                        <Grid item xs={12} md={6} key={i}>
                            <Skeleton
                                variant="rectangular"
                                height={160}
                                sx={{ borderRadius: "18px" }}
                            />
                        </Grid>
                    ))}
                </Grid>
            ) : venues.length === 0 ? (
                // Empty State
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 160,
                        backgroundColor: isDark ? "rgba(30, 41, 59, 0.5)" : "#ffffff",
                        borderRadius: "18px",
                        border: `1px dashed ${isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.1)"}`,
                        p: 3,
                    }}
                >
                    <Place sx={{ fontSize: 28, color: "text.disabled", mb: 1, opacity: 0.6 }} />
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                        No venue data available for the selected period
                    </Typography>
                </Box>
            ) : (
                // Data Grid
                <Grid container spacing={2}>
                    {venues.map((v, i) => {
                        const conv = v.total_leads
                            ? (Number(v.booked) / v.total_leads) * 100
                            : 0;

                        return (
                            <Grid
                                item
                                xs={12}
                                md={6}
                                key={v.venue_name}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: isDark ? "rgba(30, 41, 59, 0.7)" : "#ffffff",
                                        borderRadius: "18px",
                                        border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
                                        boxShadow: isDark
                                            ? "0 4px 20px rgba(0,0,0,0.3)"
                                            : "0 4px 20px rgba(0,0,0,0.03)",
                                        p: 2,
                                        transition: "all 0.2s ease",
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            justifyContent: "space-between",
                                            mb: 1.75,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: "1.05rem",
                                                    color: theme.palette.text.primary,
                                                    textTransform: "capitalize",
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                {v.venue_name}
                                            </Typography>

                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    color: isDark ? "#94a3b8" : "#64748b",
                                                    fontSize: "0.78rem",
                                                    fontWeight: 500,
                                                    mt: 0.25,
                                                    display: "block",
                                                }}
                                            >
                                                {v.venue_type || "Indoor"} · {fmtNum(v.seating_capacity)} seats
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                px: 1.5,
                                                py: 0.4,
                                                borderRadius: "999px",
                                                fontSize: "0.75rem",
                                                fontWeight: 700,
                                                color: COLORS[i % COLORS.length],
                                                backgroundColor: `${COLORS[i % COLORS.length]}18`,
                                                border: `1px solid ${COLORS[i % COLORS.length]}30`,
                                                flexShrink: 0,
                                            }}
                                        >
                                            {conv.toFixed(0)}%
                                        </Box>
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(3, 1fr)",
                                            gap: 1,
                                            textAlign: "center",
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                borderRadius: "12px",
                                                backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#f8fafc",
                                                border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"}`,
                                                py: 1,
                                                px: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    color: isDark ? "#94a3b8" : "#64748b",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.03em",
                                                    display: "block",
                                                    mb: 0.25,
                                                }}
                                            >
                                                Leads
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: "1.05rem",
                                                    color: theme.palette.text.primary,
                                                }}
                                            >
                                                {v.total_leads}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                borderRadius: "12px",
                                                backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#f8fafc",
                                                border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"}`,
                                                py: 1,
                                                px: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    color: isDark ? "#94a3b8" : "#64748b",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.03em",
                                                    display: "block",
                                                    mb: 0.25,
                                                }}
                                            >
                                                Booked
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: "1.05rem",
                                                    color: theme.palette.text.primary,
                                                }}
                                            >
                                                {v.booked}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                borderRadius: "12px",
                                                backgroundColor: isDark ? "rgba(15, 23, 42, 0.6)" : "#f8fafc",
                                                border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)"}`,
                                                py: 1,
                                                px: 0.5,
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.7rem",
                                                    color: isDark ? "#94a3b8" : "#64748b",
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.03em",
                                                    display: "block",
                                                    mb: 0.25,
                                                }}
                                            >
                                                Revenue
                                            </Typography>

                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: "0.95rem",
                                                    color: theme.palette.text.primary,
                                                }}
                                            >
                                                {fmtCurrency(v.revenue)}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            )}
        </Box>
    );
};

